import React from 'react';
import {Button, TextField, Typography, Box, Container} from '@mui/material';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {LoginSchema, loginSchema} from "../../schemas/auth/loginSchema";
import {useAuth} from "../../utils/hooks/useAuth";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {useNotification} from "../../utils/hooks/useNotification";
import PasswordTextField from "../../components/input/PasswordTextField";

const LoginForm: React.FC = () => {
    const {login} = useAuth();
    const {showNotification} = useNotification();
    const navigate = useNavigate();
    const location = useLocation();
    const {register, handleSubmit, formState: {errors}} = useForm<LoginSchema>({
        resolver: zodResolver(loginSchema)
    });

    const from = (location.state as { from?: Location })?.from || {pathname: '/'};

    const onSubmit = (data: LoginSchema) => {
        login(data.username, data.password)
            .then(() => navigate(from.pathname))
            .catch((error) => {
                console.error(error);
                if (error.response?.status === 401 || error.response?.status === 400) {
                    showNotification('Špatné uživatelské jméno nebo heslo', 'error');
                } else {
                    showNotification('Něco se pokazilo', 'error');
                }
            });
    }

    return (
        <Container component="main" maxWidth="sm" sx={{padding: {xs: 2, sm: 4}}}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mt: 8,
                    boxShadow: 5,
                    padding: {xs: 2, sm: 6},
                    borderRadius: 2,
                    backgroundColor: 'white',
                }}
            >
                <Typography component="h1" variant="h5">
                    Přihlášení
                </Typography>
                <Box
                    onSubmit={handleSubmit(onSubmit)}
                    component="form"
                    noValidate
                    sx={{mt: 3, width: '100%'}}
                >
                    <TextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        label="Uživatelské jméno"
                        autoComplete="username"
                        autoFocus
                        {...register('username')}
                        error={!!errors.username}
                        helperText={errors.username?.message}
                    />
                    <PasswordTextField
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        label="Heslo"
                        type="password"
                        autoComplete="current-password"
                        {...register('password')}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                    />
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{mt: 3, mb: 2, fontWeight: 'bold'}}
                    >
                        Přihlásit se
                    </Button>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            mt: 2,
                        }}
                    >
                        <Link to="/reset-password-request" style={{textDecoration: "none", color: "gray"}}>
                            Zapomněli jste heslo?
                        </Link>
                    </Box>
                </Box>
            </Box>
        </Container>
    );
};

export default LoginForm;
